import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaGlobe } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import nbn from '../assets/images/nbn.png'; // Adjust the path as needed

const Navbar = ({ setIsModalOpen, setIsFrameModalOpen }) => {
  const { t, i18n } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  // Handle clicks outside the dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setIsDropdownOpen(false); // Close the dropdown after selection
  };

  // Handle modal opening with error checking
  const handleModalOpen = () => {
    navigate('/'); // Go to home page first
    setTimeout(() => {
      setIsModalOpen(true); // Assume modal state is lifted up
    }, 100);
    setIsMenuOpen(false);
  };

  const handleFrameModalOpen = () => {
    navigate('/'); // Go to home page first
    setTimeout(() => {
      setIsFrameModalOpen(true); // Assume modal state is lifted up
    }, 100);
    setIsMenuOpen(false);
  };

  return (
    <nav className="bg-[#505050b6] fixed top-0 left-0 right-0 p-2 sm:p-3 md:p-4 lg:p-6 shadow-xl z-50 flex justify-between items-center">
      {/* Logo and Brand */}
      <div className="flex items-center">
        <Link to="/" className="flex items-center">
          <img
            src={nbn}
            alt="New Brindha Photography Logo"
            className="h-10 sm:h-20 md:h-20 lg:h-20 ml-2"
          />
          {/* <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 hidden sm:block">New Brindha Photography</h1> */}
        </Link>
      </div>

      {/* Hamburger Menu for Mobile */}
      <button
        className="block lg:hidden text-[#FFFF] text-xl sm:text-2xl md:text-3xl focus:outline-none hover:bg-gray-200 hover:bg-opacity-20 transition-colors duration-300 p-1 sm:p-2 mr-2 rounded-lg"
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        aria-label="Toggle menu"
      >
        ☰
      </button>

      {/* Links */}
      <div
        className={`${isMenuOpen ? 'flex' : 'hidden'
          } lg:flex absolute lg:static left-0 right-0 flex-col lg:flex-row items-center space-y-2 sm:space-y-3 top-14 lg:mt-0 md:space-y-0 md:space-x-10 p-2 sm:p-3 md:p-4 lg:p-0 shadow-md lg:shadow-none bg-gray-400 bg-opacity-50 lg:bg-transparent text-gray-900`}
      >
        <Link
          to="/"
          className="text-[#FFFF] hover:bg-gray-200 hover:bg-opacity-50 transition-colors duration-300 text-lg sm:text-xl md:text-2xl lg:text-xl font-semibold p-1 sm:p-2 rounded-md w-full lg:w-auto"
          onClick={() => setIsMenuOpen(false)}
        >
          {t('home')}
        </Link>
        <Link
          to="/photos"
          className="text-[#FFFF] hover:bg-gray-200 hover:bg-opacity-50 transition-colors duration-300 text-lg sm:text-xl md:text-2xl lg:text-xl font-semibold p-1 sm:p-2 rounded-md w-full lg:w-auto"
          onClick={() => setIsMenuOpen(false)}
        >
          {t('gallery')}
        </Link>

        {/* Book a Slot Now Button */}
        <button
          onClick={handleModalOpen}
          className="text-[#FFFF] hover:bg-gray-200 hover:bg-opacity-50 transition-colors duration-300 text-lg sm:text-xl md:text-2xl lg:text-xl font-semibold p-1 sm:p-2 rounded-md w-full lg:w-auto"
        >
          {t('bookSlot')}
        </button>

        {/* Photo Frames Button */}
        <button
          onClick={handleFrameModalOpen}
          className="text-[#FFFF] hover:bg-gray-200 hover:bg-opacity-50 transition-colors duration-300 text-lg sm:text-xl md:text-2xl lg:text-xl font-semibold p-1 sm:p-2 rounded-md w-full lg:w-auto"
        >
          {t('photoFrames')}
        </button>

        <Link
          to="/loginprivate"
          className="text-[#FFFF] hover:bg-gray-200 hover:bg-opacity-50 transition-colors duration-300 text-lg sm:text-xl md:text-2xl lg:text-xl font-semibold p-1 sm:p-2 rounded-md w-full lg:w-auto"
          onClick={() => setIsMenuOpen(false)}
        >
          {t('selectPhotos')}
        </Link>

        <div className="relative" ref={dropdownRef}>
          {/* Globe Icon */}
          <button
            className="text-2xl sm:text-3xl md:text-4xl lg:text-3xl text-[#FFFF] hover:text-gray-200 hover:opacity-50 focus:outline-none transition-colors p-1 sm:p-2 rounded-md duration-300 sm:md:mr-0 lg:mr-10"
            onClick={() => setIsDropdownOpen((prev) => !prev)}
            aria-label="Language selection"
            aria-haspopup="true"
            aria-expanded={isDropdownOpen}
          >
            <FaGlobe />
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div
              className="absolute right-0 mt-2 w-32 sm:w-36 md:w-40 lg:w-40 bg-white border border-gray-700 rounded-lg shadow-lg z-10"
            >
              <button
                className="block w-full text-left px-2 sm:px-3 md:px-4 py-1 sm:py-2 text-sm sm:text-base md:text-lg hover:bg-gray-500 hover:bg-opacity-20 transition-colors duration-300"
                onClick={() => changeLanguage('en')}
                role="menuitem"
              >
                English
              </button>
              <button
                className="block w-full text-left px-2 sm:px-3 md:px-4 py-1 sm:py-2 text-sm sm:text-base md:text-lg hover:bg-gray-500 hover:bg-opacity-20 transition-colors duration-300"
                onClick={() => changeLanguage('ta')}
                role="menuitem"
              >
                தமிழ்
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;